import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Search, Upload, Check } from "lucide-react";
import { Button } from "../../components/Button";
import { IconPickerModalProps, IconPickerTab, SelectedIcon } from "./types";
import {
  lucideIcons,
  lucideIconCategories,
  LucideIcon,
  LucideIconCategory,
  buildLucideIconUrl,
  searchLucideIcons,
  getIconsByCategory,
} from "./data/lucideIcons";
import { getIconPickerStyles } from "./iconPickerStyles";
import { getPortalContainer } from "./utils";

export const IconPickerModal: React.FC<IconPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<IconPickerTab>("upload");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIcon, setSelectedIcon] = useState<SelectedIcon | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<
    LucideIconCategory | "all"
  >("all");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [customIconName, setCustomIconName] = useState("");
  const [svgPreview, setSvgPreview] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("upload");
      setSearchQuery("");
      setSelectedIcon(null);
      setSelectedCategory("all");
      setUploadedFile(null);
      setCustomIconName("");
      setSvgPreview(null);
      setImagePreviewUrl(null);
    }
  }, [isOpen]);

  // Filter icons based on search and category
  const filteredIcons = useMemo(() => {
    let icons: LucideIcon[];

    if (searchQuery) {
      icons = searchLucideIcons(searchQuery);
    } else if (selectedCategory === "all") {
      icons = lucideIcons;
    } else {
      icons = getIconsByCategory(selectedCategory);
    }

    return icons;
  }, [searchQuery, selectedCategory]);

  // Group icons by category for display
  const iconsByCategory = useMemo(() => {
    if (searchQuery || selectedCategory !== "all") {
      return {
        [selectedCategory === "all" ? "results" : selectedCategory]:
          filteredIcons,
      };
    }

    const groups: Record<string, LucideIcon[]> = {};
    lucideIconCategories.forEach((category) => {
      groups[category] = getIconsByCategory(category);
    });
    return groups;
  }, [filteredIcons, searchQuery, selectedCategory]);

  const handleLucideIconSelect = (icon: LucideIcon) => {
    setSelectedIcon({
      type: "lucide",
      name: icon.name,
      url: buildLucideIconUrl(icon.name),
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      setCustomIconName(nameWithoutExt);

      // Revoke previous object URL to avoid memory leaks
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
        setImagePreviewUrl(null);
      }
      setSvgPreview(null);

      const isSvg = file.type === "image/svg+xml" || file.name.endsWith(".svg");
      if (isSvg) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setSvgPreview(event.target?.result as string);
        };
        reader.readAsText(file);
      } else {
        setImagePreviewUrl(URL.createObjectURL(file));
      }

      setSelectedIcon({
        type: "upload",
        name: nameWithoutExt,
        file,
      });
    }
  };

  const handleCustomNameChange = (name: string) => {
    setCustomIconName(name);
    if (selectedIcon?.type === "upload" && uploadedFile) {
      setSelectedIcon({
        ...selectedIcon,
        name,
      });
    }
  };

  const handleConfirm = () => {
    if (selectedIcon) {
      onSelect(selectedIcon);
      onClose();
    }
  };

  const isSelectDisabled =
    isLoading ||
    !selectedIcon ||
    (selectedIcon.type === "upload" &&
      (!uploadedFile || !customIconName.trim()));

  if (!isOpen) return null;

  const modalContent = (
    <>
      <style>{getIconPickerStyles()}</style>
      <div className="icon-picker-overlay" onClick={onClose}>
        <div className="icon-picker-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="icon-picker-header">
            <h2 className="icon-picker-title">Select icon</h2>
            <Button
              variant="icon"
              size="sm"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={20} />
            </Button>
          </div>

          {/* Search */}
          <div className="icon-picker-search">
            <Search size={16} className="icon-picker-search-icon" />
            <input
              type="text"
              placeholder="Search icons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="icon-picker-search-input"
              autoFocus
            />
          </div>

          {/* Tabs */}
          <div className="icon-picker-tabs">
            <button
              className={`icon-picker-tab ${activeTab === "lucide" ? "active" : ""}`}
              onClick={() => setActiveTab("lucide")}
            >
              Lucide Icons
            </button>
            <button
              className={`icon-picker-tab ${activeTab === "upload" ? "active" : ""}`}
              onClick={() => setActiveTab("upload")}
            >
              Upload Image
            </button>
          </div>

          {/* Content */}
          <div className="icon-picker-content">
            {activeTab === "lucide" && (
              <>
                {/* Category Filter */}
                <div className="icon-picker-categories">
                  <button
                    className={`icon-picker-category-btn ${selectedCategory === "all" ? "active" : ""}`}
                    onClick={() => setSelectedCategory("all")}
                  >
                    All
                  </button>
                  {lucideIconCategories.map((category) => (
                    <button
                      key={category}
                      className={`icon-picker-category-btn ${selectedCategory === category ? "active" : ""}`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Icons Grid */}
                <div className="icon-picker-icons-container">
                  {Object.entries(iconsByCategory).map(([category, icons]) => (
                    <div
                      key={category}
                      className="icon-picker-category-section"
                    >
                      {!searchQuery && selectedCategory === "all" && (
                        <h4 className="icon-picker-category-title">
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </h4>
                      )}
                      <div className="icon-picker-grid">
                        {icons.map((icon) => (
                          <div
                            key={icon.name}
                            className={`icon-picker-icon-item ${
                              selectedIcon?.type === "lucide" &&
                              selectedIcon?.name === icon.name
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => handleLucideIconSelect(icon)}
                            title={icon.name}
                          >
                            <img
                              src={buildLucideIconUrl(icon.name)}
                              alt={icon.name}
                              className="icon-picker-icon-preview"
                            />
                            {selectedIcon?.type === "lucide" &&
                              selectedIcon?.name === icon.name && (
                                <div className="icon-picker-icon-check">
                                  <Check size={12} />
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {filteredIcons.length === 0 && (
                    <div className="icon-picker-empty">
                      <p>No icons found for "{searchQuery}"</p>
                    </div>
                  )}
                </div>

                {/* Selected Icon Info */}
                {selectedIcon?.type === "lucide" && (
                  <div className="icon-picker-selected-info">
                    <img
                      src={selectedIcon.url}
                      alt={selectedIcon.name}
                      className="icon-picker-selected-preview"
                    />
                    <span className="icon-picker-selected-name">
                      {selectedIcon.name}
                    </span>
                  </div>
                )}
              </>
            )}

            {activeTab === "upload" && (
              <div className="icon-picker-upload">
                <p className="icon-picker-description">
                  Upload your own image or icon file.
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp,.svg,image/*"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />

                {!uploadedFile ? (
                  <div
                    className="icon-picker-upload-zone"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={32} className="icon-picker-upload-icon" />
                    <span className="icon-picker-upload-text">
                      Click to upload image
                    </span>
                    <span className="icon-picker-upload-hint">
                      PNG, JPG, GIF, WEBP, SVG
                    </span>
                  </div>
                ) : (
                  <div className="icon-picker-upload-preview">
                    <div className="icon-picker-upload-file">
                      {svgPreview && (
                        <div
                          className="icon-picker-image-preview"
                          dangerouslySetInnerHTML={{ __html: svgPreview }}
                        />
                      )}
                      {imagePreviewUrl && (
                        <div className="icon-picker-image-preview">
                          <img src={imagePreviewUrl} alt={customIconName} />
                        </div>
                      )}
                      <div className="icon-picker-upload-file-info">
                        <span className="icon-picker-upload-filename">
                          {uploadedFile.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                    <div className="icon-picker-upload-name">
                      <label className="icon-picker-upload-label">
                        Image name
                      </label>
                      <input
                        type="text"
                        value={customIconName}
                        onChange={(e) => handleCustomNameChange(e.target.value)}
                        placeholder="Enter image name"
                        className="icon-picker-upload-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="icon-picker-footer">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isSelectDisabled}
            >
              {isLoading ? "Adding..." : "Select"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  // Portal to parent document when in iframe to escape mobile frame constraints
  return ReactDOM.createPortal(modalContent, getPortalContainer());
};
